/**
 * Single source of truth for chain and contract configuration.
 *
 * The values arrive from the server at runtime (see `runtime-config.ts`), but
 * nothing here trusts them blindly and nothing silently falls back to a default
 * address: an unset address surfaces as `null` and the UI refuses to offer the
 * action rather than pointing a user at address zero.
 */
import { getAddress, isAddress, type Address } from "viem";

import { runtimeConfig } from "./runtime-config";

const RUNTIME_CONFIG = runtimeConfig();

export const BSC_MAINNET_CHAIN_ID = 56;
export const BSC_TESTNET_CHAIN_ID = 97;
/** Local Hardhat node, for rehearsing the full flow before a testnet deploy. */
export const LOCAL_CHAIN_ID = 31337;

export const CHAIN_ID = RUNTIME_CONFIG.chainId;

export type ClaimMode = "swap";

const configuredClaimMode = RUNTIME_CONFIG.claimMode.trim().toLowerCase();

if (configuredClaimMode !== "swap") {
  throw new Error(`claimMode must be "swap", got: ${configuredClaimMode}`);
}

export const CLAIM_MODE = configuredClaimMode as ClaimMode;

function optionalAddress(value: string | null, label: string): Address | null {
  if (!value || value.trim() === "") return null;
  if (!isAddress(value, { strict: false })) {
    throw new Error(`${label} is set but is not a valid address: ${value}`);
  }
  return getAddress(value);
}

/**
 * Legacy token AsetquSwap pulls from.
 *
 * On mainnet the address is compiled in, and the server's value is checked
 * against it rather than used. It is a published fact, and a wrong value here
 * would point the swap contract's `approve` step at a phishing token — which is
 * exactly the mistake that moving configuration to runtime would otherwise make
 * easier to introduce, since it no longer requires a rebuild to reach users.
 *
 * Off mainnet the real contract does not exist — a rehearsal deploys its own
 * MockLegacyToken, whose address changes on every redeploy — so the server's
 * value is taken as given there.
 */
const LEGACY_TOKEN_MAINNET: Address = getAddress(
  "0xC69456817C99Efa2d33F4C2408D350fC86138f76",
);

function resolveLegacyToken(): Address | null {
  if (CHAIN_ID !== BSC_MAINNET_CHAIN_ID) {
    return optionalAddress(RUNTIME_CONFIG.legacyToken, "legacyToken");
  }

  const supplied = optionalAddress(RUNTIME_CONFIG.legacyToken, "legacyToken");
  if (supplied !== null && supplied !== LEGACY_TOKEN_MAINNET) {
    // Refusing to start beats rendering a swap flow around the wrong token. A
    // disagreement here means the server and this bundle do not agree on what
    // AsetquSwap pulls from, and only one of them can be right.
    throw new Error(
      `legacyToken on mainnet must be ${LEGACY_TOKEN_MAINNET}, server sent ${supplied}`,
    );
  }
  return LEGACY_TOKEN_MAINNET;
}

export const LEGACY_TOKEN: Address | null = resolveLegacyToken();

export const ASETQU_TOKEN = optionalAddress(RUNTIME_CONFIG.asetquToken, "asetquToken");
export const CLAIM_CONTRACT = optionalAddress(RUNTIME_CONFIG.claimContract, "claimContract");
export const PERMANENT_LOCK_VAULT = optionalAddress(
  RUNTIME_CONFIG.permanentLockVault,
  "permanentLockVault",
);
/** `AsetquUtilityRegistry`. Null until the registry is deployed, which every
 *  registry page must handle — "we cannot check" is a different answer from
 *  "this is not official". */
export const UTILITY_REGISTRY = optionalAddress(
  RUNTIME_CONFIG.utilityRegistry,
  "utilityRegistry",
);

export const STAKING = optionalAddress(RUNTIME_CONFIG.staking, "staking");

/**
 * The Safe that holds the registry's admin roles.
 *
 * The console reads this only to *build* transactions for it and to link to
 * them; the portal holds no owner key and collects no signatures. Null means
 * the console has no multisig to drive and will say so rather than fall back to
 * signing directly, because falling back is how a rehearsal stops rehearsing
 * the thing that matters.
 */
export const REGISTRY_SAFE = optionalAddress(RUNTIME_CONFIG.registrySafe, "registrySafe");

/**
 * The Safe interface the owners of `REGISTRY_SAFE` actually work in.
 *
 * Every review, signature and execution happens there — `multisig.bnbchain.org`
 * on testnet, `app.safe.global` on mainnet. The portal proposes and then links
 * out, so these three helpers are the whole of its involvement in signing.
 *
 * Empty when the chain has no Safe interface, which is the local case. Callers
 * check for `null` and say there is nowhere to sign yet rather than rendering a
 * link to `/transactions/queue?safe=:0x…`.
 */
export const SAFE_APP_URL = RUNTIME_CONFIG.safeAppUrl || null;
export const SAFE_APP_PREFIX = RUNTIME_CONFIG.safeAppPrefix || null;

/** `bnb:0x1234…` — how a Safe interface names an account in a query string. */
function safeHandle(safeAddress: Address): string | null {
  if (!SAFE_APP_URL || !SAFE_APP_PREFIX) return null;
  return encodeURIComponent(`${SAFE_APP_PREFIX}:${safeAddress}`);
}

export function safeAppHome(safeAddress: Address): string | null {
  const handle = safeHandle(safeAddress);
  return handle && `${SAFE_APP_URL}/home?safe=${handle}`;
}

export function safeAppQueue(safeAddress: Address): string | null {
  const handle = safeHandle(safeAddress);
  return handle && `${SAFE_APP_URL}/transactions/queue?safe=${handle}`;
}

/**
 * A link straight to one transaction's signing page.
 *
 * `multisig_<safe>_<safeTxHash>` is the id every Safe client gateway files a
 * multisig transaction under; verified against BNB Chain's gateway on
 * 2026-08-11. The address in the id is checksummed, which is why this takes the
 * `Address` the config already normalised rather than a raw string.
 */
export function safeAppTx(safeAddress: Address, safeTxHash: string): string | null {
  const handle = safeHandle(safeAddress);
  if (!handle) return null;
  const id = `multisig_${safeAddress}_${safeTxHash}`;
  return `${SAFE_APP_URL}/transactions/tx?safe=${handle}&id=${id}`;
}

export const EXPLORER_BASE = RUNTIME_CONFIG.explorerBase;
export const NETWORK_NAME = RUNTIME_CONFIG.networkName;

/** Matches the on-chain `symbol()` exactly, legacy and new alike (D-001). */
export const TOKEN_SYMBOL = "AsetQu";
export const TOKEN_DECIMALS = 18;

/** Fixed supply of the new token, in raw units. */
export const NEW_TOTAL_SUPPLY = 9_999_999n * 10n ** 18n;

export const SUPPORT_URL = RUNTIME_CONFIG.supportUrl || null;

/**
 * Canonical public origin, for URLs that have to survive leaving the page.
 *
 * Empty falls back to the browser's own origin, which is right in development
 * and behind a correctly-configured proxy alike. The server sets it explicitly
 * in production so that anything it generates — NFT metadata in particular —
 * carries an origin a marketplace can still resolve months later.
 */
export const PORTAL_ORIGIN = RUNTIME_CONFIG.portalOrigin || null;

/** The origin to publish in links, preferring the configured one. */
export function portalOrigin(): string {
  if (PORTAL_ORIGIN) return PORTAL_ORIGIN;
  return typeof window === "undefined" ? "" : window.location.origin;
}

export function explorerAddress(address: string): string {
  return `${EXPLORER_BASE}/address/${address}`;
}

export function explorerTx(hash: string): string {
  return `${EXPLORER_BASE}/tx/${hash}`;
}
